/**
 * Hook que liga as camadas vetoriais ao mapa. O app-core chama uma vez
 * (ex.: no componente do mapa ou no App):
 *
 *   useVectorLayers();
 *
 * Pega o mapa de `useMapInstance`, adiciona as camadas ligadas no store
 * (baixando os dados sob demanda), aplica os estilos e remove tudo no cleanup.
 */
import { useEffect } from 'react';
import { useMapInstance } from '../map/mapContext';
import { VectorLayerManager } from './layerManager';
import { ensureManifest, retryFailed } from './loader';
import { useVectorStore } from './store';

export function useVectorLayers(): void {
  const map = useMapInstance((s) => s.map);

  useEffect(() => {
    if (!map) return;
    const manager = new VectorLayerManager(map);
    void ensureManifest().catch(() => {
      // O erro fica registrado no store e aparece no painel.
    });
    manager.sync(useVectorStore.getState());

    // Agrupa várias mudanças seguidas (ex.: arrastar um slider) numa só sincronização.
    let scheduled = false;
    const unsubscribe = useVectorStore.subscribe(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        manager.sync(useVectorStore.getState());
      });
    });

    // Conexão voltou: tenta de novo tudo o que falhou (as camadas ligadas recarregam).
    const onOnline = () => retryFailed();
    window.addEventListener('online', onOnline);

    return () => {
      window.removeEventListener('online', onOnline);
      unsubscribe();
      manager.dispose();
    };
  }, [map]);
}
